<template>
  <div class="cf-node-properties__input">
    <div
      v-on-clickaway="away" 
      class="voice-selector"
    >
      <span 
        class="btn btn-default form-control voice-selector__search-toggle"
        @click="toggleInput"
      >
        <span v-show="selectedVoice">
          {{ selectedVoiceFormattedName }}
        </span>
        <i class="caret" />
      </span>

      <div
        v-show="inputEnabled"
        class="voice-selector__explore-container"
      >
        <Tabs
          v-model="activeTab"
          custom-nav-class="nav-stacked"
          class="voice-selector__tabs-container"
        >
          <Tab
            :title="$t('MALE')"
            index="0"
          >
            <div class="voice-selector__tree">
              <div
                v-for="voice in voices.filter((voice) => voice.gender === 'Male')"
                :key="voice.voiceId"
                class="voice-selector__tree-gender"
              >
                <span
                  :class="{ active: selectedVoice.voiceId === voice.voiceId }"
                  class="voice-name"
                  @click="selectVoice(voice)"
                >
                  {{ voice.name }}
                </span>
                <a
                  v-if="samplePaused && voice.sample" 
                  href="javascript:void(0);" 
                  class="btn btn-function right m-t-5 m-r-20" 
                  @click="play(voice.sample)"
                >
                  <i class="fa fa-volume-down" />
                </a>
                <a
                  v-if="!samplePaused && voice.sample" 
                  href="javascript:void(0);" 
                  class="btn btn-function right m-t-5 m-r-20" 
                  @click="pause(voice.sample)"
                >
                  <i
                    class="fa"
                    :class="{ 'fa-volume-down': (sample.src !== voice.sample),
                              'fa-pause': (sample.src === voice.sample)
                    }"
                  />
                </a>
              </div>
            </div>
          </Tab>
          <Tab
            :index="1"
            :title="$t('FEMALE')"
          >
            <div class="voice-selector__tree">
              <div
                v-for="voice in voices.filter((voice) => voice.gender === 'Female')"
                :key="voice.voiceId"
                class="voice-selector__tree-gender"
              >
                <span
                  class="voice-name"
                  :class="{ active: selectedVoice.voiceId === voice.voiceId }"
                  @click="selectVoice(voice)"
                >
                  {{ voice.name }}
                </span>
                <a
                  v-if="samplePaused && voice.sample" 
                  href="javascript:void(0);" 
                  class="btn btn-function right m-t-5 m-r-20" 
                  @click="play(voice.sample)"
                >
                  <i class="fa fa-volume-down" />
                </a>
                <a
                  v-if="!samplePaused && voice.sample" 
                  href="javascript:void(0);" 
                  class="btn btn-function right m-t-5 m-r-20" 
                  @click="pause(voice.sample)"
                >
                  <i
                    class="fa"
                    :class="{ 'fa-volume-down': (sample.src !== voice.sample),
                              'fa-pause': (sample.src === voice.sample)
                    }"
                  />
                </a>
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>
    </div>

    <div
      v-if="errorMessage" 
      class="cf-node-properties__input-error"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { isEqual } from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';

import messages from '../../../../../../core/components/Form/locales';
import sharedMessages from '@/core/locales';

export default {
  mixins: [ clickaway ],
  props: {
    value: {
      type: String,
      default() { return null; }
    },
    voices: {
      type: Array,
      default() { return []; }
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    const voice = this.voices?.find((voice) => voice.voiceId === this.value);
    
    return {
      activeTab: voice && voice.gender === 'Male' ? 0 : 1,
      selectedVoice: voice,
      inputEnabled: false,
      samplePaused: true,
      sample: {},
    };
  },
  computed: {
    errorMessage() {
      return this.error?.errorMessage;
    },
    selectedVoiceFormattedName() {
      if (this.selectedVoice) {
        return this.selectedVoice.gender
        + ' - ' 
        + this.selectedVoice.name;
      }

      return '';
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if(!isEqual(newValue, oldValue)) {
          this.selectedVoice = this.voices?.find((voice) => voice.voiceId === newValue);
        }
      }
    },
    voices: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!isEqual(newValue, oldValue)) {
            this.selectedVoice = newValue.find(v => v.voiceId === this.selectedVoice?.voiceId) ?? newValue.find(v => v.voiceId === 'WOMAN');
        }
      }
    }
  },
  mounted () {
    // We do this so the ValidationProvider can validate our custom component's v-model
    // the default events when ValidationProvider does validation are `blur` and `input`
    this.$emit('blur');
  },
  methods: {
    selectVoice(voice) {
      this.selectedVoice = voice;
      this.inputEnabled = false;
      this.$emit('input', voice.voiceId);
      this.$emit('change', voice.voiceId);
    },
    away() {
      this.activeTab = this.selectedVoice?.gender === 'Male' ? 0 : 1;
      this.inputEnabled = false;
    },
    toggleInput() {
      this.inputEnabled = !this.inputEnabled;
    },
    play(sampleUrl) {
      this.sample = new Audio(sampleUrl);
      this.sample.addEventListener('timeupdate', () => {
        if (this.sample.currentTime - this.sample.duration === 0) {
          this.sample.pause();
          this.samplePaused = true;
        }
      }, false);
      
      this.sample.play();
      this.samplePaused = false;
    },
    pause(sampleUrl) {
      if (sampleUrl === this.sample.src) {
        this.sample.pause();
        this.samplePaused = true;
        this.sample = {};
        // call play for case where sample icon is pressed while other sample is playing
        // all icons call pause function when sample is in playing state
      } else {
        this.play(sampleUrl);
      }
      
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>
